<template>
  <div id="join-jci-members-list">
    <v-overlay :value="OverlayLoadingFlag">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Dialogs -->
    <v-dialog v-model="addRecordPrompt" persistent max-width="70%">
      <join-jci-member
        pageTitle="Link JCI Member"
        :addRecordPrompt="addRecordPrompt"
        @hideAddRecordPrompt="hideAddRecordPrompt"
        :LomCode="LomCode"
        :recordData="EditRowData"
        v-if="addRecordPrompt"
      ></join-jci-member>
    </v-dialog>

    <!-- Premium Modern Header Card -->
    <v-card elevation="0" class="header-card mb-8 overflow-hidden modern-header">
      <div class="header-gradient">
        <div class="header-backdrop-effect"></div>
        <div class="header-content pa-8">
          <v-row align="center" justify="space-between">
            <!-- Left Side: Title and Icon -->
            <v-col cols="12" md="8" class="d-flex align-center">
              <div class="header-icon-box icon-animate">
                <v-icon size="48" color="white" class="header-main-icon"
                  >mdi-account-multiple-check</v-icon
                >
              </div>
              <div class="ml-4">
                <h1 class="header-title">{{ PageTitle }}</h1>
                <p class="header-subtitle">
                  <v-icon size="16" class="mr-1">mdi-shield-check-outline</v-icon>
                  Manage JCI member linkage for prospective members
                </p>
              </div>
            </v-col>

            <!-- Right Side: Action Buttons -->
            <v-col cols="12" md="4" class="text-right">
              <v-spacer />
              <v-btn
                medium
                @click.prevent="refreshPageData"
                color="#a4c639"
                elevation="0"
                rounded
                class="font-size-h6 mr-3 my-3 white--text action-btn btn-hover-lift"
              >
                <v-icon left class="icon-spin-hover">mdi-refresh</v-icon>
                Refresh
              </v-btn>
              <router-link to="/mission-one-lakh/dashboard">
                <v-btn
                  medium
                  color="#90be6d"
                  elevation="0"
                  rounded
                  class="font-size-h6 mr-3 my-3 white--text action-btn btn-hover-lift"
                >
                  <v-icon left class="icon-slide-left">mdi-arrow-left</v-icon>
                  Back
                </v-btn>
              </router-link>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

    <!-- Progress & Achievement Card - PREMIUM MASSIVE -->
    <v-card elevation="24" class="progress-card mb-8 mega-achievement-card">
      <v-container class="pa-10">
        <v-row>
          <v-col cols="12">
            <div class="progress-header mega-header">
              <div class="header-icon-circle">
                <v-icon size="32" class="header-icon">mdi-target</v-icon>
              </div>
              <h3 class="progress-title mega-progress-title">Phase 4 Achievement</h3>
              <div class="header-decoration"></div>
            </div>
          </v-col>
        </v-row>

        <v-row class="mt-8">
          <v-col cols="12">
            <!-- Target requirement -->
            <div class="target-info-mega mb-8">
              <p class="target-label-mega">Target Required:</p>
              <div class="target-display-mega">
                <span class="target-number-mega">50</span>
                <span class="target-text-mega">Members Growth needed</span>
              </div>
            </div>

            <!-- Achievement status message -->
            <div
              v-if="MolSettingValues.IncreaseFlag"
              class="achievement-success-mega"
              :key="MolSettingValues.IncreaseFlag"
            >
              <!-- Popper Confetti Animation Container -->
              <div class="poppers-container" ref="poppersContainer">
                <div class="popper" v-for="n in 50" :key="n">
                  <span class="popper-emoji">🎉</span>
                </div>
              </div>

              <!-- Fireworks Animation Container -->
              <div class="fireworks-container-mega">
                <div class="firework-mega" v-for="n in 40" :key="`fw-${n}`"></div>
              </div>

              <div class="success-content-wrapper">
                <v-icon size="64" class="success-icon-mega">mdi-check-circle</v-icon>
                <h4 class="success-title-mega">🎉 Achievement Unlocked!</h4>
                <p class="success-message-mega">
                  🏆 Congratulations! You have successfully achieved the target of <span class="highlight">50 members growth</span>. 
                  <br> Phase 4 is now complete! This is a remarkable achievement that showcases your dedication and leadership.
                </p>

                <!-- Share Achievement Button - Premium -->
                <div class="achievement-actions-mega mt-8">
                  <v-btn
                    @click="captureAndShareAchievement"
                    color="#1db954"
                    x-large
                    rounded
                    elevation="16"
                    class="white--text share-btn-mega"
                  >
                    <v-icon left size="28">mdi-share-variant</v-icon>
                    Share Achievement
                  </v-btn>
                </div>
              </div>
            </div>

            <div v-else class="achievement-pending-mega">
              <v-icon size="56" class="pending-icon-mega">mdi-alert-circle</v-icon>
              <h4 class="pending-title-mega">Target Not Yet Achieved</h4>
              <p class="pending-message-mega">
                You still need
                <strong class="needed-count-mega">{{ remainingCount }}</strong>
                more members to achieve the target and complete Phase 4.
              </p>
              <p class="pending-subtitle-mega">Keep working towards the goal! 💪</p>
              
              <!-- Progress indicator -->
              <div class="progress-indicator-mega mt-8">
                <v-progress-linear
                  :value="progressPercentage"
                  :color="progressColor"
                  height="12"
                  class="progress-bar-mega"
                ></v-progress-linear>
                <div class="progress-text-mega">
                  <span class="progress-number">{{ currentIncrease }} / 50</span>
                  <span class="progress-percentage">{{ Math.round(progressPercentage) }}%</span>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <!-- Metrics Cards Grid - MASSIVE PREMIUM LOOK -->
    <v-row class="mb-8 metrics-row premium-metrics">
      <!-- Card 1: Previous Year Count - MASSIVE -->
      <v-col cols="12" sm="12" md="6" lg="4" class="mb-6">
        <v-card elevation="0" class="metric-card previous-year-card mega-card">
          <div class="card-accent-bar accent-bar-left"></div>
          <div class="card-glow-effect"></div>
          <v-container class="pa-8">
            <v-row align="center" class="no-gutters">
              <v-col cols="12" md="3" class="d-flex align-center">
                <div class="metric-badge mega-badge">
                  <div class="badge-glow"></div>
                  <v-icon size="48" class="badge-icon">mdi-history</v-icon>
                </div>
              </v-col>
              <v-col cols="12" md="9">
                <div class="metric-header mt-3 mt-md-0 ml-md-6">
                  <h3 class="metric-title mega-title">Previous Year</h3>
                  <p class="metric-subtitle mega-subtitle">Last year active members performance</p>
                </div>
              </v-col>
            </v-row>
            
            <v-row class="mt-8">
              <v-col cols="12">
                <div class="metric-value-wrapper mega-wrapper">
                  <div class="metric-value mega-value mega-value-red">
                    {{ MolSettingValues.PreviousActiveMembersCount }}
                  </div>
                </div>
              </v-col>
            </v-row>

            <v-row class="mt-6">
              <v-col cols="12">
                <div class="metric-footer mega-footer">
                  <v-icon size="20" class="footer-icon mr-2">mdi-account-multiple</v-icon>
                  <span class="footer-text">Active Members from Previous Year</span>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-col>

      <!-- Card 2: Current Year Count - MASSIVE -->
      <v-col cols="12" sm="12" md="6" lg="4" class="mb-6">
        <v-card elevation="0" class="metric-card current-year-card mega-card">
          <div class="card-accent-bar accent-bar-left"></div>
          <div class="card-glow-effect"></div>
          <v-container class="pa-8">
            <v-row align="center" class="no-gutters">
              <v-col cols="12" md="3" class="d-flex align-center">
                <div class="metric-badge mega-badge">
                  <div class="badge-glow"></div>
                  <v-icon size="48" class="badge-icon">mdi-calendar-check</v-icon>
                </div>
              </v-col>
              <v-col cols="12" md="9">
                <div class="metric-header mt-3 mt-md-0 ml-md-6">
                  <h3 class="metric-title mega-title">Current Year</h3>
                  <p class="metric-subtitle mega-subtitle">This year active members progress</p>
                </div>
              </v-col>
            </v-row>
            
            <v-row class="mt-8">
              <v-col cols="12">
                <div class="metric-value-wrapper mega-wrapper">
                  <div class="metric-value mega-value mega-value-green">
                    {{ MolSettingValues.CurrentActiveMembersCount }}
                  </div>
                </div>
              </v-col>
            </v-row>

            <v-row class="mt-6">
              <v-col cols="12">
                <div class="metric-footer mega-footer">
                  <v-icon size="20" class="footer-icon mr-2">mdi-account-check</v-icon>
                  <span class="footer-text">Active Members This Year</span>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-col>

      <!-- Card 3: Sponsor LO Count - MASSIVE -->
      <v-col cols="12" sm="12" md="12" lg="4" class="mb-6">
        <v-card elevation="0" class="metric-card sponsor-card mega-card">
          <div class="card-accent-bar accent-bar-left"></div>
          <div class="card-glow-effect"></div>
          <v-container class="pa-8">
            <v-row align="center" class="no-gutters">
              <v-col cols="12" md="3" class="d-flex align-center">
                <div class="metric-badge mega-badge">
                  <div class="badge-glow"></div>
                  <v-icon size="48" class="badge-icon">mdi-star-circle</v-icon>
                </div>
              </v-col>
              <v-col cols="12" md="9">
                <div class="metric-header mt-3 mt-md-0 ml-md-6">
                  <h3 class="metric-title mega-title">Sponsor LOs</h3>
                  <p class="metric-subtitle mega-subtitle">Affiliated organizations & members</p>
                </div>
              </v-col>
            </v-row>
            
            <v-row class="mt-8">
              <v-col cols="12">
                <div class="metric-value-wrapper mega-wrapper">
                  <div class="metric-value mega-value mega-value-blue">
                    {{ MolSettingValues.SponsorDetailsCount }}
                  </div>
                </div>
              </v-col>
            </v-row>

            <v-row class="mt-6">
              <v-col cols="12">
                <div class="metric-footer mega-footer">
                  <v-icon size="20" class="footer-icon mr-2">mdi-account-star</v-icon>
                  <span class="footer-text">Total Sponsor Members</span>
                </div>
              </v-col>
            </v-row>

            <!-- Sponsor LO Details List - Enhanced -->
            <v-row class="mt-8" v-if="
              MolSettingValues.SponsorLoDetails &&
              MolSettingValues.SponsorLoDetails.length > 0
            ">
              <v-col cols="12">
                <div class="sponsor-lo-list-mega">
                  <div class="sponsor-lo-header-mega">
                    <v-icon size="20" class="mr-2">mdi-briefcase-multiple</v-icon>
                    <span class="header-text">Affiliated Organizations</span>
                  </div>
                  <div class="sponsor-lo-items-container-mega">
                    <div
                      v-for="lo in MolSettingValues.SponsorLoDetails"
                      :key="lo.LomId"
                      class="sponsor-lo-item-mega"
                    >
                      <div class="lo-info-mega">
                        <div class="lo-icon-mega">
                          <v-icon size="24">mdi-domain</v-icon>
                        </div>
                        <div class="lo-details-mega">
                          <div class="lo-name-mega">{{ lo.LomName }}</div>
                        </div>
                      </div>
                      <div class="lo-badge-mega">
                        <span class="badge-count-mega">{{ lo.LomCount }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
            <v-row class="mt-8" v-else>
              <v-col cols="12">
                <div class="sponsor-lo-list-mega">
                  <div class="sponsor-lo-empty-mega">
                    <v-icon size="28">mdi-information-outline</v-icon>
                    <span class="ml-3">No organizations available</span>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="false">
      <!-- Information Card -->
      <v-card elevation="12" class="info-card mb-6">
        <v-container class="pa-6">
          <v-row>
            <v-col cols="12" md="6">
              <div class="info-item">
                <v-icon color="#e74c3c" class="mr-2">mdi-alert-circle</v-icon>
                <span class="font-weight-bold">DeadLine Date:</span>
                <span class="ml-2">{{ MolSettingValues.DeadLineDateTxt }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item">
                <v-icon color="#1db954" class="mr-2"
                  >mdi-checkbox-marked-circle</v-icon
                >
                <span class="font-weight-bold">Minimum Required:</span>
                <span class="ml-2"
                  >{{ MolSettingValues.MinimumCount }} members</span
                >
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card>

      <!-- Instructions Card -->
      <v-card elevation="12" class="instructions-card mb-6">
        <v-container class="pa-8">
          <v-row>
            <v-col cols="12">
              <h4 class="instruction-heading">
                <v-icon class="mr-2">mdi-information-outline</v-icon>
                Phase Actions & Requirements
              </h4>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <div class="instruction-content">
                <p class="instruction-paragraph">
                  <strong>Goal:</strong> Now, turn those prospects into members!
                  The goal is to inspire at least 50 out of the 100 prospects to
                  officially join your Local Organization.
                </p>

                <div class="requirement-box">
                  <v-icon color="#4caf50" class="flex-shrink-0"
                    >mdi-check-circle</v-icon
                  >
                  <span class="requirement-text"
                    >Select a prospective member from the list</span
                  >
                </div>

                <div class="requirement-box">
                  <v-icon color="#4caf50" class="flex-shrink-0"
                    >mdi-check-circle</v-icon
                  >
                  <span class="requirement-text"
                    >Link them with an existing JCI member to complete
                    onboarding</span
                  >
                </div>

                <p class="instruction-paragraph">
                  <strong>⚡ Action:</strong> Pay the membership subscription
                  for 50 new members on the portal.
                </p>

                <p class="instruction-paragraph reward">
                  <strong>🏆 Reward:</strong> Final milestone recognition
                  unlocked!
                </p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card>

      <!-- Data Table Card -->
      <v-card
        elevation="12"
        class="table-card"
        v-if="!StartupLoadingFlag && rows.length > 0"
      >
        <v-container class="pa-6">
          <v-row class="mb-4">
            <v-col cols="12" class="d-flex align-center justify-space-between">
              <h3 class="table-title">
                <v-icon class="mr-2">mdi-list-box-outline</v-icon>
                {{ rows.length }} Members Found
              </h3>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search..."
                single-line
                hide-details
                outlined
                dense
                class="search-field"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-data-table
            class="members-table"
            :headers="TableColumns1"
            :items="rows"
            :search="search"
            :item-key="TableOptions1.ItemKey"
            :items-per-page="TableOptions1.ItemsPerPage"
            :footer-props="TableOptions1.FooterProps"
          >
            <template v-slot:item.actions="{ item }">
              <v-tooltip bottom>
                <template
                  v-slot:activator="{ on, attrs }"
                  v-if="item.Phase6CreateFlag"
                >
                  <v-btn
                    icon
                    small
                    color="primary"
                    @click.prevent="addMemberAlert(item)"
                    v-bind="attrs"
                    v-on="on"
                    title="Link Member"
                    class="action-icon-btn"
                  >
                    <v-icon small>mdi-account-multiple-plus</v-icon>
                  </v-btn>
                </template>
                <span>Link Member</span>
              </v-tooltip>
              &nbsp;&nbsp;
              <v-tooltip bottom>
                <template
                  v-slot:activator="{ on, attrs }"
                  v-if="item.Phase6DeleteFlag"
                >
                  <v-btn
                    icon
                    small
                    color="red"
                    @click="deleteData(item)"
                    v-bind="attrs"
                    v-on="on"
                    title="Delete"
                    class="action-icon-btn"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>Delete</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-container>
      </v-card>
    </v-row>
    <!-- Loading State -->
    <v-container class="py-0" v-if="StartupLoadingFlag">
      <v-row wrap>
        <v-col cols="12" md="12" lg="12">
          <div class="d-flex justify-content-center mb-3">
            <b-spinner
              variant="primary"
              type="grow"
              label="Loading..."
            ></b-spinner>
          </div>
          <v-overlay
            v-model="OverlayLoadingFlag"
            contained
            class="align-center justify-center"
          ></v-overlay>
        </v-col>
      </v-row>
    </v-container>

    <!-- Search Loading State -->
    <v-container class="py-0" v-if="SearchFlag && !StartupLoadingFlag">
      <v-row wrap>
        <v-col cols="12" md="12" lg="12">
          <v-skeleton-loader
            v-bind="attrs"
            type="table-tbody"
          ></v-skeleton-loader>
        </v-col>
      </v-row>
    </v-container>

    <!-- Empty State -->
    <v-card
      elevation="12"
      class="empty-state-card"
      v-if="!SearchFlag && !StartupLoadingFlag && rows.length == 0"
    >
      <v-container class="pa-12 text-center">
        <v-icon size="80" color="grey lighten-2">mdi-inbox-outline</v-icon>
        <h3 class="mt-4 text-h6 grey--text text--darken-2">No records found</h3>
        <p class="mt-2 text-body2 grey--text">
          All prospective members have been linked with JCI members.
        </p>
      </v-container>
    </v-card>
  </div>
</template>
        
          <script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import JoinJciMember from "@/view/pages/erp/lom/mission-onelakh/mol-member/JoinJciMember.vue";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {
    JoinJciMember,
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      StartupLoadingFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      ResultFlag: false,
      TableSelectFlag: false,
      OverlayLoadingFlag: false,
      LomCode: "",
      rows: [],
      TableColumns1: [],
      TableOptions1: {},
      selected: [],
      search: "",
      Phase: "",

      addRecordPrompt: false,
      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      MolSettingValues: [],
      shareImageUrl: "",
    };
  },
  mounted() {},
  computed: {
    currentIncrease() {
      if (
        !this.MolSettingValues.CurrentTotalCount ||
        !this.MolSettingValues.PreviousActiveMembersCount
      ) {
        return 0;
      }
      const increase =
        this.MolSettingValues.CurrentTotalCount -
        this.MolSettingValues.PreviousActiveMembersCount;
      return increase > 0 ? increase : 0;
    },
    remainingCount() {
      const remaining = 50 - this.currentIncrease;
      return remaining > 0 ? remaining : 0;
    },
    progressPercentage() {
      return Math.min((this.currentIncrease / 50) * 100, 100);
    },
    progressColor() {
      if (this.MolSettingValues.IncreaseFlag) {
        return "#1db954"; // Green
      } else if (this.progressPercentage >= 75) {
        return "#ffa500"; // Orange
      } else if (this.progressPercentage >= 50) {
        return "#ff9800"; // Orange
      } else {
        return "#e74c3c"; // Red
      }
    },
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = CurrentJciYearId;

        var LomId = this.$session.get("LomId");
        LomId = LomId == (null || undefined) ? "" : LomId;
        this.LomCode = LomId;
        console.log("LomCode=" + this.LomCode);

        var ZoneCode = this.$session.get("ZoneId");
        ZoneCode = ZoneCode == (null || undefined) ? "" : ZoneCode;
        console.log("ZoneCode=" + ZoneCode);
        this.ZoneCode = ZoneCode;

        this.pageData();
      }
    },
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "mol_phase6",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("pageData called");
      this.StartupLoadingFlag = true;
      this.getMolSetting();
      this.getTableRecords();
    },
    // code 1
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    hideAddRecordPrompt(flag) {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      if (flag) {
        this.getTableRecords();
      }
    },
    resetForm() {
      this.search = "";
      this.rows = [];
      this.selected = [];
      this.$refs.form3.reset();
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    getTableRecords() {
      console.log("getTableRecords called");

      var YearCode = this.JciYearCode;
      console.log("YearCode=" + YearCode);

      this.rows = [];

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/mission-onelakh/mol-members-list";
      var upload = {
        UserInterface: 6,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      this.rows = [];
      this.SearchFlag = true;
      this.LoadingFlag = true;
      this.OverlayLoadingFlag = true;

      const thisIns = this;
      var output = "";
      var records = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.SearchFlag = false;
          thisIns.LoadingFlag = false;
          thisIns.StartupLoadingFlag = false;
          thisIns.OverlayLoadingFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          records = response.data.records;
          console.log(
            "output=" + output + ", flag=" + flag + ", records=" + records
          );

          var options = records.TableOptions;
          thisIns.TableOptions1 = options;

          if (flag == 1) {
            thisIns.TableColumns1 = records.TableHeader;
            thisIns.rows = records.TableData;
            thisIns.toast("success", output);
          } else {
            thisIns.toast("error", output);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.toast("error", error);
          thisIns.SearchFlag = false;
          thisIns.LoadingFlag = false;
        });
    },
    addMemberAlert(tr) {
      console.log("editAlert called");
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        // var CityId = tr.CityId;
        // this.member = tr;
        this.EditRowData = tr;
        this.addRecordPrompt = true;
      } else {
        var message = "Kindly select one record to edit";
        this.errorMessage(message);
      }
    },
    deleteData(tr) {
      console.log("deleteData called");
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      console.log({ tr });
      if (n1 > 0) {
        Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to delete this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: `Delete`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.deleteRecord(tr);
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
        // this.deleteRecord(tr);
      } else {
        var message = "Kindly select one member to delete";
        this.toast("error", message);
      }
    },
    deleteRecord(tr) {
      // add todo in store and clear all fields in dialog
      console.log("deleteRecord called");

      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      console.log({ tr });

      if (n1 > 0) {
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/mission-onelakh/mol-member-update";
        var upload = {
          UserInterface: 9,
          MolMember: tr.MolMemberId,
          Lom: this.LomCode,
          Phase: 6,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.OverlayLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);

            if (flag == 1) {
              thisIns.sweetAlert("success", output, false);
              thisIns.getTableRecords();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        console.log("some errors occured");
        var message = "Kindly select one member to delete";
        this.sweetAlert("error", message, false);
      }
    },
    getMolSetting() {
      console.log("getMolSetting called");

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/mission-onelakh/mol-setting-show";
      var upload = {
        UserInterface: 6,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      this.SearchFlag = true;
      this.LoadingFlag = true;

      const thisIns = this;
      var output = "";
      var records = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.SearchFlag = false;
          thisIns.LoadingFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          records = response.data.records;
          console.log(
            "output=" + output + ", flag=" + flag + ", records=" + records
          );

          if (flag == 1) {
            thisIns.MolSettingValues = records;
            // thisIns.toast("success", output);
          } else {
            // thisIns.toast("error", output);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.toast("error", error);
          thisIns.SearchFlag = false;
          thisIns.LoadingFlag = false;
        });
    },
    shareAchievement() {
      // Prepare share message
      const shareTitle = "JCI Phase 6 Achievement Unlocked!";
      const shareText =
        "I have successfully completed Phase 6 of the Mission One Lakh campaign with 50+ member increase! 🎉 Join me in this exciting initiative.";
      const shareUrl = window.location.href;

      // Check if Web Share API is available
      if (navigator.share) {
        navigator
          .share({
            title: shareTitle,
            text: shareText,
            url: shareUrl,
          })
          .then(() => {
            this.sweetAlert(
              "success",
              "Achievement shared successfully!",
              false
            );
          })
          .catch((error) => {
            if (error.name !== "AbortError") {
              console.log("Error sharing:", error);
            }
          });
      } else {
        // Fallback: Copy to clipboard and show alternatives
        const textToCopy = `${shareTitle}\n${shareText}\n${shareUrl}`;
        navigator.clipboard
          .writeText(textToCopy)
          .then(() => {
            this.showShareOptions(shareTitle, shareText, shareUrl);
          })
          .catch(() => {
            this.sweetAlert(
              "info",
              "Share this achievement with your network!",
              false
            );
          });
      }
    },
    showShareOptions(title, text, url) {
      // Social media share URLs
      const encodedText = encodeURIComponent(`${title}\n${text}`);
      const encodedUrl = encodeURIComponent(url);

      const shareLinks = {
        whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      };

      // Show a dialog with share options
      Swal.fire({
        title: "Share Your Achievement",
        html: `
          <div style="text-align: center; padding: 20px;">
            <p style="margin-bottom: 20px; font-size: 14px; color: #666;">
              Copy link (already copied!) or share directly:
            </p>
            <div style="display: flex; gap: 12px; justify-content: center; flex-wrap: wrap;">
              <a href="${shareLinks.whatsapp}" target="_blank" rel="noopener noreferrer" 
                 style="display: inline-flex; align-items: center; gap: 8px; padding: 10px 16px; background: #25D366; color: white; border-radius: 8px; text-decoration: none; font-weight: 600; cursor: pointer;">
                <span style="font-size: 18px;">💬</span> WhatsApp
              </a>
              <a href="${shareLinks.facebook}" target="_blank" rel="noopener noreferrer"
                 style="display: inline-flex; align-items: center; gap: 8px; padding: 10px 16px; background: #1877F2; color: white; border-radius: 8px; text-decoration: none; font-weight: 600; cursor: pointer;">
                <span style="font-size: 18px;">f</span> Facebook
              </a>
              <a href="${shareLinks.twitter}" target="_blank" rel="noopener noreferrer"
                 style="display: inline-flex; align-items: center; gap: 8px; padding: 10px 16px; background: #1DA1F2; color: white; border-radius: 8px; text-decoration: none; font-weight: 600; cursor: pointer;">
                <span style="font-size: 18px;">𝕏</span> Twitter
              </a>
              <a href="${shareLinks.linkedin}" target="_blank" rel="noopener noreferrer"
                 style="display: inline-flex; align-items: center; gap: 8px; padding: 10px 16px; background: #0A66C2; color: white; border-radius: 8px; text-decoration: none; font-weight: 600; cursor: pointer;">
                <span style="font-size: 18px;">in</span> LinkedIn
              </a>
            </div>
          </div>
        `,
        icon: "success",
        confirmButtonText: "Done",
        confirmButtonColor: "#1db954",
        allowOutsideClick: true,
        allowEscapeKey: true,
      });
    },
    captureAndShareAchievement() {
      console.log("captureAndShareAchievement called");
      this.OverlayLoadingFlag = true;

      // Create canvas for achievement image with premium design
      const canvas = document.createElement("canvas");
      canvas.width = 1400;
      canvas.height = 700;
      const ctx = canvas.getContext("2d");

      // ============================================
      // PREMIUM BACKGROUND DESIGN
      // ============================================
      // Main gradient background - Premium multi-color gradient
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, "#1a3a52");      // Deep navy
      gradient.addColorStop(0.25, "#2c5aa0");   // Rich blue
      gradient.addColorStop(0.5, "#0097d7");    // Vibrant aqua
      gradient.addColorStop(0.75, "#1db954");   // Vibrant green
      gradient.addColorStop(1, "#0d7a3a");      // Deep green
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // ============================================
      // DECORATIVE PREMIUM ELEMENTS
      // ============================================
      // Large decorative circles with glassmorphism effect
      ctx.fillStyle = "rgba(255, 255, 255, 0.08)";
      ctx.beginPath();
      ctx.arc(150, 150, 180, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = "rgba(255, 255, 255, 0.06)";
      ctx.beginPath();
      ctx.arc(1250, 550, 150, 0, Math.PI * 2);
      ctx.fill();

      // Additional accent circles
      ctx.fillStyle = "rgba(237, 190, 56, 0.1)";
      ctx.beginPath();
      ctx.arc(1350, 100, 100, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = "rgba(29, 185, 84, 0.1)";
      ctx.beginPath();
      ctx.arc(50, 600, 80, 0, Math.PI * 2);
      ctx.fill();

      // ============================================
      // PREMIUM BORDER/FRAME
      // ============================================
      ctx.strokeStyle = "rgba(255, 255, 255, 0.15)";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.roundRect(30, 30, canvas.width - 60, canvas.height - 60, 30);
      ctx.stroke();

      ctx.strokeStyle = "rgba(255, 255, 255, 0.08)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.roundRect(40, 40, canvas.width - 80, canvas.height - 80, 25);
      ctx.stroke();

      // ============================================
      // PREMIUM ACCENT BAR AT TOP
      // ============================================
      const accentGradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
      accentGradient.addColorStop(0, "rgba(237, 190, 56, 0)");
      accentGradient.addColorStop(0.5, "rgba(237, 190, 56, 0.4)");
      accentGradient.addColorStop(1, "rgba(237, 190, 56, 0)");
      ctx.fillStyle = accentGradient;
      ctx.fillRect(0, 70, canvas.width, 3);

      // ============================================
      // PREMIUM TEXT CONTENT
      // ============================================
      // Shadow effects for text
      ctx.shadowColor = "rgba(0, 0, 0, 0.3)";
      ctx.shadowBlur = 8;
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 2;

      // Emoji icon at top - Large
      ctx.font = "bold 80px Arial";
      ctx.textAlign = "center";
      ctx.fillStyle = "#ffffff";
      ctx.fillText("🏆", canvas.width / 2, 140);

      // Remove shadow for main text
      ctx.shadowColor = "transparent";

      // Main title - "Achievement Unlocked!"
      ctx.font = "bold 64px 'Segoe UI', Arial";
      ctx.textAlign = "center";
      ctx.fillStyle = "#ffffff";
      ctx.fillText("ACHIEVEMENT UNLOCKED!", canvas.width / 2, 220);

      // Divider line
      ctx.strokeStyle = "rgba(237, 190, 56, 0.5)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(canvas.width / 2 - 200, 250);
      ctx.lineTo(canvas.width / 2 + 200, 250);
      ctx.stroke();

      // Phase completion - Large and prominent
      ctx.font = "bold 56px 'Segoe UI', Arial";
      ctx.fillStyle = "#edbe38";
      ctx.fillText("Phase 4 Complete!", canvas.width / 2, 320);

      // Growth achievement - Premium styling
      ctx.font = "bold 48px 'Segoe UI', Arial";
      ctx.fillStyle = "#1db954";
      ctx.fillText("50+ Members Growth Achieved!", canvas.width / 2, 390);

      // Member count section with better typography
      ctx.font = "32px 'Segoe UI', Arial";
      ctx.fillStyle = "rgba(255, 255, 255, 0.95)";
      ctx.fillText("Current Year: ", canvas.width / 2 - 350, 465);
      ctx.font = "bold 32px 'Segoe UI', Arial";
      ctx.fillStyle = "#1db954";
      ctx.fillText(`${this.MolSettingValues.CurrentActiveMembersCount} Members`, canvas.width / 2 - 50, 465);

      // Previous year count
      ctx.font = "32px 'Segoe UI', Arial";
      ctx.fillStyle = "rgba(255, 255, 255, 0.95)";
      ctx.fillText("Previous Year: ", canvas.width / 2 - 350, 530);
      ctx.font = "bold 32px 'Segoe UI', Arial";
      ctx.fillStyle = "#e74c3c";
      ctx.fillText(`${this.MolSettingValues.PreviousActiveMembersCount} Members`, canvas.width / 2 - 50, 530);

      // ============================================
      // PREMIUM FOOTER
      // ============================================
      // Footer divider line
      ctx.strokeStyle = "rgba(237, 190, 56, 0.5)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(100, 600);
      ctx.lineTo(canvas.width - 100, 600);
      ctx.stroke();

      // JCI Mission One Lakh branding - Premium style
      ctx.font = "bold 36px 'Segoe UI', Arial";
      ctx.fillStyle = "#edbe38";
      ctx.fillText("🎯 JCI MISSION ONE LAKH", canvas.width / 2, 655);

      // Date/timestamp at very bottom
      const today = new Date().toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      ctx.font = "18px 'Segoe UI', Arial";
      ctx.fillStyle = "rgba(255, 255, 255, 0.6)";
      ctx.fillText(`Achievement Date: ${today}`, canvas.width / 2, 685);

      // ============================================
      // CONVERT & SHARE
      // ============================================
      canvas.toBlob((blob) => {
        const file = new File([blob], "jci_achievement_premium.png", {
          type: "image/png",
        });
        const url = URL.createObjectURL(blob);
        this.shareImageUrl = url;

        // Show premium share dialog
        const thisComponent = this;
        Swal.fire({
          title: "🎉 Share Your Achievement",
          html: `
            <div style="text-align: center; padding: 30px 20px; animation: slideInUp 0.6s ease-out;">
              <img src="${url}" style="max-width: 100%; width: 100%; max-height: 400px; border-radius: 16px; margin-bottom: 25px; box-shadow: 0 12px 40px rgba(0, 151, 215, 0.3), 0 6px 20px rgba(0,0,0,0.2); transition: all 0.3s ease; cursor: pointer;" onmouseover="this.style.transform='scale(1.02)'" onmouseout="this.style.transform='scale(1)'">
              <p style="font-size: 16px; color: #333; margin-bottom: 25px; font-weight: 600; letter-spacing: 0.5px;">
                Congratulations! 🎊 Your achievement image is ready.
              </p>
              <div style="display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; margin-bottom: 15px;">
                <button id="downloadBtn" onclick="
                  const link = document.createElement('a');
                  link.href = '${url}';
                  link.download = 'jci_achievement.png';
                  link.click();
                " style="display: inline-flex; align-items: center; gap: 10px; padding: 14px 24px; background: linear-gradient(135deg, #1db954 0%, #0d7a3a 100%); color: white; border: none; border-radius: 10px; font-weight: 700; cursor: pointer; font-size: 14px; transition: all 0.3s ease; box-shadow: 0 4px 16px rgba(29, 185, 84, 0.3);" onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 6px 24px rgba(29, 185, 84, 0.4)'" onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 16px rgba(29, 185, 84, 0.3)'">
                  <span style="font-size: 18px;">📥</span> Download Image
                </button>
                <button id="shareBtn" onclick="
                  fetch('${url}')
                    .then(res => res.blob())
                    .then(blob => {
                      if (navigator.share) {
                        navigator.share({
                          title: 'JCI Achievement Unlocked',
                          text: 'I have successfully completed Phase 4 of the Mission One Lakh campaign! 🎉',
                          files: [new File([blob], 'jci_achievement.png', { type: 'image/png' })]
                        }).catch(err => console.log('Share cancelled:', err));
                      } else {
                        alert('Share feature not supported on this device. Please download the image instead.');
                      }
                    })
                    .catch(err => console.error('Error:', err));
                " style="display: inline-flex; align-items: center; gap: 10px; padding: 14px 24px; background: linear-gradient(135deg, #0097d7 0%, #004a7a 100%); color: white; border: none; border-radius: 10px; font-weight: 700; cursor: pointer; font-size: 14px; transition: all 0.3s ease; box-shadow: 0 4px 16px rgba(0, 151, 215, 0.3);" onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 6px 24px rgba(0, 151, 215, 0.4)'" onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 16px rgba(0, 151, 215, 0.3)'">
                  <span style="font-size: 18px;">🔗</span> Share via Social
                </button>
              </div>
              <p style="font-size: 12px; color: #999; margin-top: 15px; font-style: italic;">
                💡 Share your achievement and inspire others! ✨
              </p>
            </div>
          `,
          icon: "success",
          confirmButtonText: "Close",
          confirmButtonColor: "#1db954",
          allowOutsideClick: true,
          allowEscapeKey: true,
          didOpen: (modal) => {
            modal.style.animation = "slideInUp 0.5s ease-out";
          },
        });

        this.OverlayLoadingFlag = false;
      }, "image/png");
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
          <style lang="scss">
// Color Palette - JCI Brand Colors
$navy: #3a67b1;
$aqua: #0097d7;
$gold: #edbe38;
$seaform: #56bda3;
$text-off-black: #0a0f29;
$text-white: #ffffff;
$danger-color: #e74c3c;
$border-color: #e2e8f0;
$success-color: #4caf50;

// ============================================
// KEYFRAME ANIMATIONS
// ============================================
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-12px);
  }
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    opacity: 1;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes burst {
  0% {
    transform: translate(0, 0) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(
        calc(cos(var(--angle)) * 150px),
        calc(sin(var(--angle)) * 150px)
      )
      scale(0);
    opacity: 0;
  }
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
}

// Popper Animation
@keyframes popperBurst {
  0% {
    transform: translate(0, 0) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(
        calc(cos(var(--angle)) * 200px),
        calc(sin(var(--angle)) * 200px)
      )
      scale(0);
    opacity: 0;
  }
}

@keyframes popperSpin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes scaleInBig {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

// ============================================
// MODERN ANIMATIONS
// ============================================
@keyframes iconSpin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes iconBounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}

@keyframes iconPulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes slideLeft {
  0% {
    transform: translateX(-4px);
  }
  50% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-4px);
  }
}

@keyframes glow {
  0%, 100% {
    box-shadow: 0 0 5px rgba(0, 151, 215, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(0, 151, 215, 0.6);
  }
}

@keyframes cardHoverGlow {
  0% {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  }
  100% {
    box-shadow: 0 16px 40px rgba(0, 151, 215, 0.2);
  }
}

// ============================================
// PREMIUM MODAL ANIMATIONS
// ============================================
@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes imageReveal {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes buttonScale {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

@keyframes shimmerPremium {
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
}

@keyframes glowPulse {
  0%, 100% {
    box-shadow: 0 0 10px rgba(29, 185, 84, 0.3),
                inset 0 0 10px rgba(29, 185, 84, 0.1);
  }
  50% {
    box-shadow: 0 0 20px rgba(29, 185, 84, 0.5),
                inset 0 0 20px rgba(29, 185, 84, 0.2);
  }
}

// ============================================
// PROFESSIONAL SMOOTH ANIMATIONS
// ============================================
@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.96) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes smoothReveal {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes glideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes subtleFloat {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-6px);
  }
}

@keyframes breathe {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.95;
    transform: scale(1.02);
  }
}

@keyframes slideInDown {
  from {
    opacity: 0;
    transform: translateY(-16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes expandIn {
  from {
    opacity: 0;
    max-height: 0;
  }
  to {
    opacity: 1;
    max-height: 500px;
  }
}

// ============================================
// MAIN CONTAINER STYLES
// ============================================
#join-jci-members-list {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 30px 15px;

  // ============================================
  // HEADER CARD - MODERN DESIGN
  // ============================================
  .header-card {
    background: linear-gradient(
      135deg,
      $navy 0%,
      $aqua 50%,
      $navy 100%
    ) !important;
    border-radius: 24px;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none !important;
    box-shadow: 0 12px 40px rgba(58, 103, 177, 0.2),
      0 4px 12px rgba(0, 151, 215, 0.15);
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      box-shadow: 0 20px 60px rgba(58, 103, 177, 0.3),
        0 8px 20px rgba(0, 151, 215, 0.25);
      transform: translateY(-4px);
    }

    &.modern-header {
      background: linear-gradient(
        135deg,
        #2c5aa0 0%,
        #0084d1 50%,
        #1b70b8 100%
      ) !important;
    }

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        45deg,
        transparent 30%,
        rgba(255, 255, 255, 0.08) 50%,
        transparent 70%
      );
      animation: shimmer 4s infinite;
    }

    .header-gradient {
      position: relative;
      z-index: 1;
    }

    .header-backdrop-effect {
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.1) 0%,
        transparent 70%
      );
      animation: rotate 30s linear infinite;
    }

    .header-content {
      position: relative;
      z-index: 2;
    }
  }

  .header-icon-box {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.2) 0%,
      rgba(255, 255, 255, 0.12) 100%
    );
    backdrop-filter: blur(12px);
    border-radius: 20px;
    padding: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
    animation: float 4s ease-in-out infinite;
    border: 2px solid rgba(255, 255, 255, 0.25);
    transition: all 0.3s ease;
    flex-shrink: 0;

    &.icon-animate {
      animation: float 4s ease-in-out infinite;

      .header-main-icon {
        transition: all 0.4s ease;
      }

      &:hover {
        animation: iconPulse 0.6s ease-in-out;
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2),
          inset 0 1px 0 rgba(255, 255, 255, 0.4);
      }
    }
  }

  .header-title {
    font-size: 36px;
    font-weight: 900;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.25), 0 0 20px rgba(0, 151, 215, 0.3);
    animation: slideInLeft 0.8s ease-out;
    letter-spacing: 0.8px;
    color: $text-white;
    margin: 0;
    font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  }

  .header-subtitle {
    font-size: 16px;
    opacity: 0.95;
    animation: slideInLeft 1s ease-out 0.1s backwards;
    letter-spacing: 0.3px;
    color: rgba($text-white, 0.9);
    font-weight: 500;
    margin: 6px 0 0 0;
    display: flex;
    align-items: center;
    gap: 6px;

    .v-icon {
      animation: iconBounce 2s ease-in-out infinite;
    }
  }

  .action-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.15) !important;
    border: 2px solid rgba(255, 255, 255, 0.25) !important;
    backdrop-filter: blur(8px);
    color: white !important;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1) !important;

    &.btn-hover-lift {
      &:hover {
        transform: translateY(-6px);
        background: rgba(255, 255, 255, 0.25) !important;
        border-color: rgba(255, 255, 255, 0.4) !important;
        box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2) !important;
      }

      &:active {
        transform: translateY(-2px);
      }
    }

    .icon-spin-hover {
      transition: all 0.4s ease;
    }

    &:hover .icon-spin-hover {
      animation: iconSpin 0.6s ease-in-out;
    }

    .icon-slide-left {
      transition: all 0.4s ease;
    }

    &:hover .icon-slide-left {
      animation: slideLeft 0.6s ease-in-out;
    }
  }

  // ============================================
  // MEGA METRIC CARDS - MASSIVE PREMIUM LOOK
  // ============================================
  .premium-metrics {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 40px;

    @media (max-width: 1200px) {
      gap: 32px;
    }

    @media (max-width: 960px) {
      gap: 32px;
    }

    @media (max-width: 600px) {
      gap: 24px;
    }
  }

  .sponsor-card {
    @media (max-width: 960px) {
      width: 100%;
    }
  }

  .mega-card {
    border-radius: 20px !important;
    background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%) !important;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08),
      0 2px 8px rgba(0, 0, 0, 0.04) !important;
    border: 2px solid rgba(0, 151, 215, 0.08) !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 380px;

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.15) 0%,
        transparent 70%
      );
      animation: rotate 50s linear infinite;
    }

    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.05) 0%,
        transparent 50%,
        rgba(0, 0, 0, 0.01) 100%
      );
      pointer-events: none;
    }

    &:hover {
      transform: translateY(-12px);
      box-shadow: 0 20px 60px rgba(0, 151, 215, 0.15),
        0 8px 24px rgba(0, 0, 0, 0.1) !important;
      border-color: rgba(0, 151, 215, 0.2) !important;

      .metric-badge.mega-badge {
        animation: iconPulse 0.6s ease-in-out;

        .badge-icon {
          animation: iconBounce 0.8s ease-in-out;
        }
      }

      .footer-icon {
        animation: iconBounce 0.6s ease-in-out;
      }
    }

    .v-container {
      position: relative;
      z-index: 2;
      flex: 1;
      display: flex;
      flex-direction: column;
    }

    .card-accent-bar {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 6px;
      border-radius: 20px 20px 0 0;
      transition: all 0.3s ease;

      &.accent-bar-left {
        left: 0;
        width: 100%;
      }
    }

    .card-glow-effect {
      position: absolute;
      top: -50%;
      right: -50%;
      width: 300%;
      height: 300%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.2) 0%,
        transparent 70%
      );
      animation: float 8s ease-in-out infinite;
      filter: blur(40px);
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    &:hover .card-glow-effect {
      opacity: 0.5;
    }

    .metric-badge.mega-badge {
      width: 100px;
      height: 100px;
      border-radius: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
      animation: float 4s ease-in-out infinite;

      .badge-glow {
        position: absolute;
        inset: -10px;
        border-radius: 18px;
        opacity: 0;
        transition: opacity 0.3s ease;
        z-index: -1;
        animation: glow 2s ease-in-out infinite;
      }

      .badge-icon {
        filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.12));
        transition: all 0.3s ease;
        animation: iconBounce 3s ease-in-out infinite;
      }

      &:hover {
        transform: scale(1.15) rotate(-6deg);
        box-shadow: 0 12px 36px rgba(0, 0, 0, 0.18);
        animation: none;

        .badge-icon {
          filter: drop-shadow(0 6px 16px rgba(0, 0, 0, 0.2));
          animation: iconPulse 0.6s ease-in-out;
        }
      }
    }

    .metric-header {
      margin-bottom: 8px;

      &.mega-header {
        margin-bottom: 0;
      }

      .metric-title.mega-title {
        font-size: 28px;
        font-weight: 900;
        letter-spacing: 1.2px;
        margin: 0;
        text-transform: uppercase;
        line-height: 1.1;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
      }

      .metric-subtitle.mega-subtitle {
        font-size: 14px;
        font-weight: 600;
        opacity: 0.75;
        margin: 8px 0 0 0;
        letter-spacing: 0.6px;
      }
    }

    .metric-value-wrapper.mega-wrapper {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      margin: 24px 0;
      min-height: 120px;

      .metric-value.mega-value {
        font-size: 120px;
        font-weight: 900;
        font-variant-numeric: tabular-nums;
        letter-spacing: -4px;
        line-height: 1;
        text-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
        animation: scaleInBig 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);

        &.mega-value-red {
          background: linear-gradient(135deg, #e74c3c, #c0392b);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        &.mega-value-green {
          background: linear-gradient(135deg, #1db954, #0b6d2a);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        &.mega-value-blue {
          background: linear-gradient(135deg, #0097d7, #0066a1);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      }
    }

    .metric-footer.mega-footer {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 14px;
      font-weight: 700;
      letter-spacing: 0.8px;
      text-transform: uppercase;
      opacity: 0.85;
      margin-top: auto;

      .footer-icon {
        opacity: 0.8;
        animation: float 3s ease-in-out infinite;
      }

      .footer-text {
        font-size: 13px;
        line-height: 1.4;
      }
    }
  }

  // Mega card color variants
  .previous-year-card.mega-card {
    background: linear-gradient(135deg, #fff5f5 0%, #ffe8e8 100%) !important;
    border-color: rgba(231, 76, 60, 0.15) !important;

    .card-accent-bar {
      background: linear-gradient(90deg, #e74c3c 0%, #d63031 100%);
    }

    .metric-badge.mega-badge {
      background: linear-gradient(
        135deg,
        rgba(231, 76, 60, 0.2),
        rgba(231, 76, 60, 0.1)
      );

      .badge-icon {
        color: #e74c3c;
      }

      .badge-glow {
        background: radial-gradient(circle, rgba(231, 76, 60, 0.3), transparent);
      }
    }

    .metric-title.mega-title {
      color: #c0392b;
    }

    .metric-subtitle.mega-subtitle {
      color: #e74c3c;
    }

    .metric-footer.mega-footer {
      color: #c0392b;
    }

    &:hover {
      background: linear-gradient(135deg, #fff0f0 0%, #ffd9d9 100%) !important;
    }
  }

  .current-year-card.mega-card {
    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%) !important;
    border-color: rgba(29, 185, 84, 0.15) !important;

    .card-accent-bar {
      background: linear-gradient(90deg, #1db954 0%, #16a34a 100%);
    }

    .metric-badge.mega-badge {
      background: linear-gradient(
        135deg,
        rgba(29, 185, 84, 0.2),
        rgba(29, 185, 84, 0.1)
      );

      .badge-icon {
        color: #1db954;
      }

      .badge-glow {
        background: radial-gradient(circle, rgba(29, 185, 84, 0.3), transparent);
      }
    }

    .metric-title.mega-title {
      color: #0b6d2a;
    }

    .metric-subtitle.mega-subtitle {
      color: #1db954;
    }

    .metric-footer.mega-footer {
      color: #0b6d2a;
    }

    &:hover {
      background: linear-gradient(135deg, #f5fdfa 0%, #ccf0dd 100%) !important;
    }
  }

  .sponsor-card.mega-card {
    background: linear-gradient(135deg, #f0f7ff 0%, #e0f2fe 100%) !important;
    border-color: rgba(0, 151, 215, 0.15) !important;

    .card-accent-bar {
      background: linear-gradient(90deg, #0097d7 0%, #0086c9 100%);
    }

    .metric-badge.mega-badge {
      background: linear-gradient(
        135deg,
        rgba(0, 151, 215, 0.2),
        rgba(0, 151, 215, 0.1)
      );

      .badge-icon {
        color: #0097d7;
      }

      .badge-glow {
        background: radial-gradient(circle, rgba(0, 151, 215, 0.3), transparent);
      }
    }

    .metric-title.mega-title {
      color: #0066a1;
    }

    .metric-subtitle.mega-subtitle {
      color: #0097d7;
    }

    .metric-footer.mega-footer {
      color: #0066a1;
    }

    &:hover {
      background: linear-gradient(135deg, #f5fbff 0%, #cce5f0 100%) !important;
    }

    .sponsor-lo-list-mega {
      background: linear-gradient(
        135deg,
        rgba(0, 151, 215, 0.06),
        rgba(0, 151, 215, 0.03)
      );
      border: 2px solid rgba(0, 151, 215, 0.2);
      border-radius: 18px;
      padding: 20px;
      display: flex;
      flex-direction: column;
      max-height: 380px;

      .sponsor-lo-header-mega {
        font-size: 12px;
        font-weight: 800;
        color: #0097d7;
        text-transform: uppercase;
        letter-spacing: 1.2px;
        display: flex;
        align-items: center;
        margin-bottom: 18px;
        padding-bottom: 14px;
        border-bottom: 2px solid rgba(0, 151, 215, 0.25);
        flex-shrink: 0;

        .header-text {
          font-weight: 700;
        }

        .v-icon {
          flex-shrink: 0;
          margin-right: 8px;
        }
      }

      .sponsor-lo-items-container-mega {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 14px;
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        padding-right: 8px;
        min-height: 100px;
        scroll-behavior: smooth;

        scrollbar-color: rgba(0, 151, 215, 0.5) rgba(0, 151, 215, 0.08);
        scrollbar-width: thin;

        &::-webkit-scrollbar {
          width: 12px;
        }

        &::-webkit-scrollbar-track {
          background: rgba(0, 151, 215, 0.08);
          border-radius: 10px;
        }

        &::-webkit-scrollbar-thumb {
          background: linear-gradient(
            180deg,
            rgba(0, 151, 215, 0.6),
            rgba(0, 151, 215, 0.4)
          );
          border-radius: 10px;
          min-height: 40px;

          &:hover {
            background: linear-gradient(
              180deg,
              rgba(0, 151, 215, 0.8),
              rgba(0, 151, 215, 0.6)
            );
          }
        }
      }

      .sponsor-lo-empty-mega {
        font-size: 14px;
        color: #999;
        text-align: center;
        padding: 24px;
        font-weight: 500;
        background: rgba(0, 151, 215, 0.08);
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        flex-shrink: 0;
      }

      .sponsor-lo-item-mega {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 16px 14px;
        border-radius: 14px;
        background: rgba(255, 255, 255, 0.8);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        text-align: center;
        gap: 8px;
        box-shadow: 0 4px 12px rgba(0, 151, 215, 0.1);

        &:hover {
          background: linear-gradient(
            135deg,
            rgba(0, 151, 215, 0.15),
            rgba(0, 151, 215, 0.08)
          );
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0, 151, 215, 0.25);
        }

        .lo-info-mega {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          width: 100%;

          .lo-icon-mega {
            width: 36px;
            height: 36px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(
              135deg,
              rgba(0, 151, 215, 0.2),
              rgba(0, 151, 215, 0.1)
            );
            color: #0097d7;
            box-shadow: 0 4px 12px rgba(0, 151, 215, 0.15);
          }

          .lo-details-mega {
            width: 100%;

            .lo-name-mega {
              font-size: 12px;
              font-weight: 700;
              color: #0a0f29;
              white-space: normal;
              word-break: break-word;
              line-height: 1.3;
              text-transform: uppercase;
              letter-spacing: 0.4px;
            }
          }
        }

        .lo-badge-mega {
          width: 100%;

          .badge-count-mega {
            font-size: 11px;
            font-weight: 900;
            color: #ffffff;
            background: linear-gradient(135deg, #0097d7, #0086c9);
            padding: 6px 12px;
            border-radius: 14px;
            white-space: nowrap;
            box-shadow: 0 4px 16px rgba(0, 151, 215, 0.3);
            display: inline-flex;
            align-items: center;
            justify-content: center;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-variant-numeric: tabular-nums;
          }
        }
      }
    }
  }


  // ============================================
  // CARD VARIANTS
  // ============================================
  .previous-year-card {
    background: linear-gradient(135deg, #fff5f5 0%, #ffe8e8 100%) !important;
    border-color: rgba(231, 76, 60, 0.12) !important;

    .card-accent-bar {
      background: linear-gradient(90deg, #e74c3c 0%, #d63031 100%);
    }

    .metric-badge {
      background: linear-gradient(
        135deg,
        rgba(231, 76, 60, 0.15),
        rgba(231, 76, 60, 0.08)
      );

      .badge-icon {
        color: #e74c3c;
      }
    }

    .metric-title {
      color: #c0392b;
    }

    .metric-subtitle {
      color: #e74c3c;
    }

    .metric-value {
      color: #e74c3c;
    }

    .metric-footer {
      color: #c0392b;
    }

    &:hover {
      background: linear-gradient(135deg, #fff0f0 0%, #ffd9d9 100%) !important;
    }
  }

  .current-year-card {
    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%) !important;
    border-color: rgba(29, 185, 84, 0.12) !important;

    .card-accent-bar {
      background: linear-gradient(90deg, #1db954 0%, #16a34a 100%);
    }

    .metric-badge {
      background: linear-gradient(
        135deg,
        rgba(29, 185, 84, 0.15),
        rgba(29, 185, 84, 0.08)
      );

      .badge-icon {
        color: #1db954;
      }
    }

    .metric-title {
      color: #0b6d2a;
    }

    .metric-subtitle {
      color: #1db954;
    }

    .metric-value {
      color: #1db954;
    }

    .metric-footer {
      color: #0b6d2a;
    }

    &:hover {
      background: linear-gradient(135deg, #f5fdfa 0%, #ccf0dd 100%) !important;
    }
  }

  .sponsor-card {
    background: linear-gradient(135deg, #f0f7ff 0%, #e0f2fe 100%) !important;
    border-color: rgba(0, 151, 215, 0.12) !important;

    .card-accent-bar {
      background: linear-gradient(90deg, #0097d7 0%, #0086c9 100%);
    }

    .metric-badge {
      background: linear-gradient(
        135deg,
        rgba(0, 151, 215, 0.15),
        rgba(0, 151, 215, 0.08)
      );

      .badge-icon {
        color: #0097d7;
      }
    }

    .metric-title {
      color: #0066a1;
    }

    .metric-subtitle {
      color: #0097d7;
    }

    .metric-value {
      color: #0097d7;
    }

    .metric-footer {
      color: #0066a1;
    }

    &:hover {
      background: linear-gradient(135deg, #f5fbff 0%, #cce5f0 100%) !important;
    }

    .sponsor-lo-list {
      background: linear-gradient(
        135deg,
        rgba(0, 151, 215, 0.04),
        rgba(0, 151, 215, 0.02)
      );
      border: 1px solid rgba(0, 151, 215, 0.15);
      border-radius: 14px;
      padding: 12px;
      margin-top: 14px;
      display: flex;
      flex-direction: column;
      max-height: 320px;

      .sponsor-lo-header {
        font-size: 11px;
        font-weight: 800;
        color: #0097d7;
        text-transform: uppercase;
        letter-spacing: 1px;
        display: flex;
        align-items: center;
        margin-bottom: 12px;
        padding-bottom: 10px;
        border-bottom: 2px solid rgba(0, 151, 215, 0.2);
        flex-shrink: 0;

        .v-icon {
          flex-shrink: 0;
          margin-right: 6px;
        }
      }

      .sponsor-lo-items-container {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 10px;
        flex: 1;
        overflow-y: scroll;
        overflow-x: hidden;
        padding-right: 8px;
        min-height: 100px;
        scroll-behavior: smooth;

        /* Firefox scrollbar */
        scrollbar-color: rgba(0, 151, 215, 0.5) rgba(0, 151, 215, 0.08);
        scrollbar-width: thin;

        /* Webkit scrollbar */
        &::-webkit-scrollbar {
          width: 10px;
        }

        &::-webkit-scrollbar-track {
          background: rgba(0, 151, 215, 0.08);
          border-radius: 10px;
        }

        &::-webkit-scrollbar-thumb {
          background: linear-gradient(
            180deg,
            rgba(0, 151, 215, 0.6),
            rgba(0, 151, 215, 0.4)
          );
          border-radius: 10px;
          min-height: 40px;

          &:hover {
            background: linear-gradient(
              180deg,
              rgba(0, 151, 215, 0.8),
              rgba(0, 151, 215, 0.6)
            );
          }

          &:active {
            background: linear-gradient(
              180deg,
              rgba(0, 151, 215, 1),
              rgba(0, 151, 215, 0.8)
            );
          }
        }
      }

      .sponsor-lo-empty {
        font-size: 12px;
        color: #999;
        text-align: center;
        padding: 16px;
        font-weight: 500;
        background: rgba(0, 151, 215, 0.05);
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        flex-shrink: 0;
      }

      .sponsor-lo-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 12px 10px;
        border-radius: 12px;
        background: rgba(255, 255, 255, 0.7);
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        text-align: center;
        gap: 6px;

        &:hover {
          background: linear-gradient(
            135deg,
            rgba(0, 151, 215, 0.15),
            rgba(0, 151, 215, 0.08)
          );
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0, 151, 215, 0.2);
        }

        .lo-info {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          width: 100%;

          .lo-icon {
            width: 28px;
            height: 28px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(
              135deg,
              rgba(0, 151, 215, 0.15),
              rgba(0, 151, 215, 0.08)
            );
            color: #0097d7;
          }

          .lo-details {
            width: 100%;

            .lo-name {
              font-size: 11px;
              font-weight: 700;
              color: #0a0f29;
              white-space: normal;
              word-break: break-word;
              line-height: 1.2;
            }
          }
        }

        .lo-badge {
          .badge-count {
            font-size: 10px;
            font-weight: 900;
            color: #ffffff;
            background: linear-gradient(135deg, #0097d7, #0086c9);
            padding: 3px 8px;
            border-radius: 12px;
            white-space: nowrap;
            box-shadow: 0 3px 12px rgba(0, 151, 215, 0.25);
            display: inline-flex;
            align-items: center;
            justify-content: center;
            text-transform: uppercase;
            letter-spacing: 0.3px;
          }
        }
      }
    }
  }

  // ============================================
  // MEGA PROGRESS CARD - MASSIVE ACHIEVEMENT
  // ============================================
  .mega-achievement-card {
    border-radius: 32px !important;
    background: linear-gradient(135deg, #f0f7ff 0%, #e8f4f8 100%) !important;
    box-shadow: 0 24px 120px rgba(58, 103, 177, 0.25),
      0 12px 48px rgba(0, 151, 215, 0.2) !important;
    border: 3px solid rgba(0, 151, 215, 0.3) !important;
    animation: slideInUp 1s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.2) 0%,
        transparent 70%
      );
      animation: rotate 60s linear infinite;
      z-index: 1;
    }

    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.15) 0%,
        transparent 50%,
        rgba(0, 0, 0, 0.03) 100%
      );
      pointer-events: none;
      z-index: 1;
    }

    .v-container {
      position: relative;
      z-index: 2;
    }
  }

  .progress-card {
    border-radius: 24px !important;
    background: linear-gradient(135deg, #f0f7ff 0%, #e8f4f8 100%) !important;
    box-shadow: 0 12px 40px rgba(58, 103, 177, 0.15) !important;
    border: 2px solid rgba(0, 151, 215, 0.2) !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s backwards;

    .progress-header {
      display: flex;
      align-items: center;
      margin-bottom: 24px;

      .v-icon {
        color: #0097d7;
        animation: float 4s ease-in-out infinite;
      }

      .progress-title {
        font-size: 24px;
        font-weight: 800;
        color: #0066a1;
        margin: 0;
        text-transform: uppercase;
        letter-spacing: 1px;
      }
    }

    .progress-header.mega-header {
      position: relative;
      margin-bottom: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 20px;

      .header-icon-circle {
        width: 80px;
        height: 80px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, #0097d7, #0086c9);
        box-shadow: 0 12px 40px rgba(0, 151, 215, 0.3),
          inset 0 1px 0 rgba(255, 255, 255, 0.3);
        animation: pulse 2s ease-in-out infinite, float 4s ease-in-out infinite;
        position: relative;
        border: 3px solid rgba(255, 255, 255, 0.4);

        .header-icon {
          color: #ffffff;
          animation: float 4s ease-in-out infinite;
        }
      }

      .mega-progress-title {
        font-size: 32px;
        font-weight: 900;
        color: #0066a1;
        text-transform: uppercase;
        letter-spacing: 1.5px;
        text-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        margin: 0;
        flex: 1;
      }

      .header-decoration {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background: linear-gradient(135deg, rgba(237, 190, 56, 0.2), rgba(237, 190, 56, 0.1));
        animation: rotate 8s linear infinite;
      }
    }

    .target-info-mega {
      background: linear-gradient(
        135deg,
        rgba(0, 151, 215, 0.12),
        rgba(0, 151, 215, 0.06)
      );
      padding: 24px;
      border-radius: 18px;
      border-left: 6px solid #0097d7;
      box-shadow: 0 8px 24px rgba(0, 151, 215, 0.1);

      .target-label-mega {
        font-size: 14px;
        font-weight: 800;
        color: #0066a1;
        text-transform: uppercase;
        letter-spacing: 0.8px;
        margin: 0 0 12px 0;
      }

      .target-display-mega {
        display: flex;
        align-items: baseline;
        gap: 16px;

        .target-number-mega {
          font-size: 64px;
          font-weight: 900;
          color: #0097d7;
          line-height: 1;
          text-shadow: 0 4px 12px rgba(0, 151, 215, 0.2);
        }

        .target-text-mega {
          font-size: 16px;
          font-weight: 700;
          color: #0a0f29;
          line-height: 1.3;
        }
      }
    }

    .achievement-success-mega {
      text-align: center;
      padding: 40px 32px;
      background: linear-gradient(
        135deg,
        rgba(29, 185, 84, 0.15),
        rgba(29, 185, 84, 0.08)
      );
      border-radius: 20px;
      border: 3px solid rgba(29, 185, 84, 0.35);
      animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
      position: relative;
      overflow: hidden;
      box-shadow: 0 16px 64px rgba(29, 185, 84, 0.15);

      .poppers-container {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        overflow: hidden;

        .popper {
          position: absolute;
          font-size: 32px;
          pointer-events: none;
          animation: popperBurst 1.5s ease-out forwards;
          opacity: 0;

          @for $i from 1 through 50 {
            &:nth-child(#{$i}) {
              left: 50%;
              top: 50%;
              --angle: #{($i * 7.2deg)};
              animation-delay: #{($i * 0.05s)};
            }
          }
        }
      }

      .fireworks-container-mega {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        overflow: hidden;
      }

      .firework-mega {
        position: absolute;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        pointer-events: none;
        animation: burst 1.4s ease-out forwards;

        @for $i from 1 through 40 {
          &:nth-child(#{$i}) {
            left: 50%;
            top: 50%;
            background: #{nth(
                (#ff6b6b, #ffd93d, #6bcf7f, #4ecdc4, #ff85a1, #ffd700, #ff7f50, #ff1493),
                random(8)
              )};
            --angle: #{($i * 9deg)};
            animation-delay: #{($i * 0.03s)};
          }
        }
      }

      .success-content-wrapper {
        position: relative;
        z-index: 2;
      }

      .success-icon-mega {
        color: #1db954;
        animation: pulse 2s ease-in-out infinite, bounce 1s ease-out;
        display: block;
        margin-bottom: 20px;
        filter: drop-shadow(0 8px 20px rgba(29, 185, 84, 0.3));
      }

      .success-title-mega {
        font-size: 32px;
        font-weight: 900;
        color: #0b6d2a;
        margin: 0 0 16px 0;
        text-transform: uppercase;
        letter-spacing: 1.2px;
        text-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      }

      .success-message-mega {
        font-size: 18px;
        line-height: 1.8;
        color: #0a0f29;
        margin: 0 0 12px 0;
        font-weight: 600;

        .highlight {
          font-size: 20px;
          font-weight: 900;
          color: #1db954;
          background: linear-gradient(
            135deg,
            rgba(29, 185, 84, 0.15),
            rgba(29, 185, 84, 0.08)
          );
          padding: 6px 12px;
          border-radius: 8px;
          display: inline-block;
          margin: 0 4px;
          box-shadow: 0 4px 12px rgba(29, 185, 84, 0.15);
        }
      }

      .achievement-actions-mega {
        position: relative;
        z-index: 2;

        .share-btn-mega {
          font-weight: 800;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 12px 36px rgba(29, 185, 84, 0.35) !important;
          border-radius: 20px !important;
          padding: 16px 48px !important;
          min-height: 64px;

          &:hover {
            transform: translateY(-6px) scale(1.08);
            box-shadow: 0 16px 48px rgba(29, 185, 84, 0.45) !important;
          }

          &:active {
            transform: translateY(-2px) scale(0.96);
          }
        }
      }
    }

    .achievement-pending-mega {
      text-align: center;
      padding: 40px 32px;
      background: linear-gradient(
        135deg,
        rgba(231, 76, 60, 0.12),
        rgba(231, 76, 60, 0.06)
      );
      border-radius: 20px;
      border: 3px solid rgba(231, 76, 60, 0.3);
      animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
      box-shadow: 0 16px 64px rgba(231, 76, 60, 0.12);

      .pending-icon-mega {
        color: #e74c3c;
        animation: pulse 2s ease-in-out infinite;
        display: block;
        margin-bottom: 20px;
        filter: drop-shadow(0 8px 20px rgba(231, 76, 60, 0.25));
      }

      .pending-title-mega {
        font-size: 28px;
        font-weight: 900;
        color: #c0392b;
        margin: 0 0 16px 0;
        text-transform: uppercase;
        letter-spacing: 1px;
        text-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      }

      .pending-message-mega {
        font-size: 16px;
        line-height: 1.8;
        color: #0a0f29;
        margin: 0 0 12px 0;
        font-weight: 600;

        .needed-count-mega {
          font-size: 24px;
          font-weight: 900;
          color: #ffffff;
          display: inline-block;
          background: linear-gradient(135deg, #e74c3c, #c0392b);
          padding: 8px 16px;
          border-radius: 10px;
          margin: 0 6px;
          box-shadow: 0 6px 16px rgba(231, 76, 60, 0.3);
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
      }

      .pending-subtitle-mega {
        font-size: 15px;
        color: #666;
        font-style: italic;
        margin: 12px 0 0 0;
        font-weight: 500;
      }

      .progress-indicator-mega {
        margin-top: 28px;

        .progress-bar-mega {
          border-radius: 12px !important;
          box-shadow: 0 6px 20px rgba(58, 103, 177, 0.2) !important;

          &::v-deep {
            .v-progress-linear__fill {
              animation: shimmer 2s infinite;
            }
          }
        }

        .progress-text-mega {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 12px;
          padding: 0 12px;

          .progress-number {
            font-size: 18px;
            font-weight: 800;
            color: #0097d7;
            text-transform: uppercase;
            letter-spacing: 0.6px;
          }

          .progress-percentage {
            font-size: 18px;
            font-weight: 800;
            color: #e74c3c;
            text-transform: uppercase;
            letter-spacing: 0.6px;
          }
        }
      }
    }

    .achievement-pending {
      text-align: center;
      padding: 24px;
      background: linear-gradient(
        135deg,
        rgba(231, 76, 60, 0.1),
        rgba(231, 76, 60, 0.05)
      );
      border-radius: 14px;
      border: 2px solid rgba(231, 76, 60, 0.3);
      animation: slideInUp 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);

      .pending-icon {
        color: #e74c3c;
        animation: pulse 2s ease-in-out infinite;
        display: block;
        margin-bottom: 16px;
      }

      .pending-title {
        font-size: 22px;
        font-weight: 800;
        color: #c0392b;
        margin: 0 0 12px 0;
        text-transform: uppercase;
        letter-spacing: 1px;
      }

      .pending-message {
        font-size: 15px;
        line-height: 1.6;
        color: #0a0f29;
        margin: 0 0 8px 0;
        font-weight: 500;

        .needed-count {
          font-size: 18px;
          font-weight: 900;
          color: #e74c3c;
          display: inline-block;
          background: rgba(231, 76, 60, 0.15);
          padding: 4px 10px;
          border-radius: 8px;
          margin: 0 4px;
        }
      }

      .pending-subtitle {
        font-size: 13px;
        color: #666;
        font-style: italic;
        margin: 0;
      }
    }
  }

  // ============================================
  // INFO CARD
  // ============================================
  .info-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #e8f4f8 0%, #f0f7ff 100%) !important;
    box-shadow: 0 8px 24px rgba(58, 103, 177, 0.1) !important;
    border: 2px solid rgba(0, 151, 215, 0.2) !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s backwards;

    .info-item {
      display: flex;
      align-items: center;
      padding: 12px;
      background: rgba(255, 255, 255, 0.6);
      border-radius: 8px;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.9);
        transform: translateX(4px);
      }
    }
  }

  // ============================================
  // INSTRUCTIONS CARD
  // ============================================
  .instructions-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #fff5e6 0%, #fffaf0 100%) !important;
    box-shadow: 0 12px 32px rgba(237, 190, 56, 0.15) !important;
    border: 2px solid rgba(237, 190, 56, 0.2) !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s backwards;

    .instruction-heading {
      font-size: 18px;
      font-weight: 700;
      color: $navy;
      display: flex;
      align-items: center;
      margin: 0 0 16px 0;
      text-transform: uppercase;
      letter-spacing: 0.5px;

      .v-icon {
        transition: all 0.3s ease;
      }

      &:hover .v-icon {
        transform: rotate(20deg) scale(1.15);
      }
    }

    .instruction-content {
      .requirement-box {
        display: flex;
        align-items: center;
        gap: 16px;
        background: linear-gradient(
          135deg,
          rgba(76, 175, 80, 0.12),
          rgba(76, 175, 80, 0.08)
        );
        border-left: 5px solid $success-color;
        padding: 16px 14px;
        border-radius: 12px;
        transition: all 0.3s ease;
        margin-bottom: 12px;

        &:hover {
          background: linear-gradient(
            135deg,
            rgba(76, 175, 80, 0.15),
            rgba(76, 175, 80, 0.1)
          );
          box-shadow: 0 6px 16px rgba(76, 175, 80, 0.15);
          transform: translateY(-2px);
        }

        .v-icon {
          flex-shrink: 0;
          transition: all 0.3s ease;
        }

        &:hover .v-icon {
          transform: scale(1.2);
        }

        .requirement-text {
          color: $text-off-black;
          font-weight: 600;
          font-size: 14px;
          line-height: 1.6;
        }
      }

      .instruction-paragraph {
        font-size: 15px;
        line-height: 1.6;
        color: $text-off-black;
        margin: 12px 0;
        padding: 10px 12px;
        background: rgba(255, 255, 255, 0.7);
        border-left: 4px solid $gold;
        border-radius: 4px;
        transition: all 0.3s ease;

        &:hover {
          background: rgba(255, 255, 255, 0.95);
          transform: translateX(4px);
          box-shadow: 0 4px 12px rgba(237, 190, 56, 0.1);
        }

        &.reward {
          border-left-color: #1db954;
          background: rgba(29, 185, 84, 0.05);
        }
      }
    }
  }

  // ============================================
  // TABLE CARD
  // ============================================
  .table-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    box-shadow: 0 16px 48px rgba(58, 103, 177, 0.12) !important;
    border: none !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s backwards;
  }

  .table-title {
    font-size: 18px;
    font-weight: 700;
    color: $navy;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
  }

  .search-field {
    transition: all 0.3s ease;
    border-radius: 8px;
    max-width: 300px;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
    }

    &::v-deep input {
      font-size: 14px !important;
    }
  }

  .members-table {
    &::v-deep {
      .v-data-table__wrapper {
        border-radius: 12px;
        overflow: hidden;
      }

      thead {
        background: linear-gradient(135deg, $navy 0%, $aqua 100%) !important;
        box-shadow: 0 4px 12px rgba(58, 103, 177, 0.15);

        th {
          color: white !important;
          font-weight: 800 !important;
          letter-spacing: 0.8px !important;
          text-transform: uppercase !important;
          font-size: 12px !important;
          padding: 16px 12px !important;
          background: inherit !important;

          &::before {
            display: none !important;
          }
        }
      }

      tbody {
        background: #fafbfc;

        tr {
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          border-bottom: 1px solid rgba(58, 103, 177, 0.06) !important;

          &:hover {
            background-color: linear-gradient(
              90deg,
              rgba(0, 151, 215, 0.04),
              rgba(0, 151, 215, 0.02)
            ) !important;
            box-shadow: inset 0 0 0 1px rgba(0, 151, 215, 0.1) !important;
          }

          &:last-child {
            border-bottom: none !important;
          }

          td {
            color: $text-off-black;
            font-weight: 500;
            padding: 14px 12px !important;
            font-size: 13px;
          }
        }

        tr:nth-child(even) {
          background-color: rgba(58, 103, 177, 0.02) !important;
        }

        tr:nth-child(odd) {
          background-color: #ffffff !important;
        }
      }

      .v-data-table__checkbox {
        margin-right: 8px;

        .v-simple-checkbox {
          .v-icon {
            color: $aqua !important;
          }
        }
      }

      .v-data-footer {
        background: linear-gradient(
          135deg,
          rgba(58, 103, 177, 0.03),
          rgba(0, 151, 215, 0.02)
        ) !important;
        border-top: 1px solid rgba(58, 103, 177, 0.1) !important;
        padding: 12px 16px !important;

        .v-data-footer__select {
          .v-select {
            margin-top: 0;
          }
        }

        .v-data-footer__pagination {
          color: $navy;
          font-weight: 600;
        }
      }
    }
  }

  // ============================================
  // ACTION BUTTONS - ENHANCED
  // ============================================
  .action-icon-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    background: linear-gradient(
      135deg,
      rgba(0, 0, 0, 0.04),
      rgba(0, 0, 0, 0.02)
    ) !important;
    border-radius: 10px !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important;

    &:hover {
      transform: scale(1.18) !important;
      background: linear-gradient(
        135deg,
        rgba(0, 0, 0, 0.08),
        rgba(0, 0, 0, 0.04)
      ) !important;
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12) !important;
    }

    &:active {
      transform: scale(0.95) !important;
    }
  }

  // ============================================
  // RESPONSIVE ADJUSTMENTS
  // ============================================
  @media (max-width: 960px) {
    .metric-card {
      .metric-value {
        font-size: 40px !important;
      }

      .metric-title {
        font-size: 14px !important;
      }
    }

    .members-table {
      &::v-deep {
        thead th {
          font-size: 11px !important;
          padding: 12px 8px !important;
        }

        tbody td {
          font-size: 12px !important;
          padding: 10px 8px !important;
        }
      }
    }
  }

  @media (max-width: 600px) {
    .metric-card {
      .metric-badge {
        width: 52px;
        height: 52px;

        .badge-icon {
          font-size: 24px;
        }
      }

      .metric-title {
        font-size: 13px !important;
      }

      .metric-subtitle {
        font-size: 11px !important;
      }

      .metric-value {
        font-size: 36px !important;
      }

      .metric-footer {
        font-size: 10px !important;
      }
    }

    .sponsor-lo-list {
      max-height: 220px;
      .sponsor-lo-item {
        width: 100%;
        .lo-details .lo-name {
          font-size: 11px !important;
        }

        .lo-badge .badge-count {
          font-size: 10px !important;
          padding: 3px 8px !important;
        }
      }
    }

    .members-table {
      &::v-deep {
        thead th {
          font-size: 10px !important;
          padding: 10px 6px !important;
        }

        tbody td {
          font-size: 11px !important;
          padding: 8px 6px !important;
        }

        .v-data-table__checkbox {
          margin-right: 4px;
        }
      }
    }
  }

  // ============================================
  // EMPTY STATE
  // ============================================
  .empty-state-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    box-shadow: 0 16px 48px rgba(58, 103, 177, 0.12) !important;
    border: none !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s backwards;

    &::v-deep {
      .v-icon {
        animation: pulse 2s ease-in-out infinite;
      }
    }
  }

  // ============================================
  // RESPONSIVE DESIGN
  // ============================================
  @media (max-width: 960px) {
    padding: 20px 12px;

    .header-title {
      font-size: 28px;
    }

    .header-subtitle {
      font-size: 14px;
    }

    .header-icon-box {
      padding: 10px;
    }

    .table-title {
      font-size: 16px;
    }

    .metric-value {
      font-size: 32px !important;
    }

    .metric-title {
      font-size: 14px !important;
    }
  }

  @media (max-width: 600px) {
    padding: 16px 10px;

    .header-card {
      margin-bottom: 16px;

      .header-content {
        padding: 16px !important;
      }
    }

    .header-title {
      font-size: 22px;
    }

    .header-subtitle {
      font-size: 13px;
    }

    .search-field {
      max-width: 100% !important;
      margin-top: 12px;
    }

    .members-table {
      &::v-deep {
        font-size: 12px;

        th,
        td {
          padding: 8px !important;
        }
      }
    }

    .action-cell {
      flex-direction: column;
      gap: 3px;
    }

    .metric-card {
      margin-bottom: 12px !important;

      .metric-header {
        flex-direction: column;
        align-items: flex-start;
        text-align: left;

        .v-icon {
          margin-bottom: 8px;
        }

        .metric-title {
          font-size: 13px !important;
          margin-left: 0 !important;
        }
      }

      .metric-value {
        font-size: 36px !important;
      }

      .metric-label {
        font-size: 12px !important;
      }
    }
  }
}

// ============================================
// PREMIUM SWEETALERT2 MODAL STYLING
// ============================================
::v-deep .swal2-popup {
  animation: fadeInScale 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
  border-radius: 20px !important;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafb 100%) !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15),
              0 0 1px rgba(0, 151, 215, 0.3) !important;
  border: 2px solid rgba(0, 151, 215, 0.1) !important;
}

::v-deep .swal2-title {
  font-size: 28px !important;
  font-weight: 800 !important;
  color: #1a3a52 !important;
  letter-spacing: 1px !important;
  font-family: 'Segoe UI', Arial, sans-serif !important;
  margin-bottom: 20px !important;
  animation: slideInDown 0.6s ease-out !important;
}

::v-deep .swal2-html-container {
  animation: smoothReveal 0.6s ease-out 0.2s backwards !important;
}

::v-deep .swal2-icon {
  animation: subtleFloat 2.5s ease-in-out infinite !important;
}

::v-deep .swal2-confirm {
  animation: glideIn 0.5s ease-out 0.3s backwards !important;
  background: linear-gradient(135deg, #1db954 0%, #0d7a3a 100%) !important;
  border: none !important;
  border-radius: 10px !important;
  padding: 12px 32px !important;
  font-weight: 700 !important;
  font-size: 14px !important;
  box-shadow: 0 6px 20px rgba(29, 185, 84, 0.3) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;

  &:hover {
    transform: translateY(-3px) !important;
    box-shadow: 0 12px 30px rgba(29, 185, 84, 0.4) !important;
  }

  &:active {
    transform: translateY(-1px) !important;
  }
}

::v-deep .swal2-container {
  z-index: 9999 !important;
}
</style>
